export type MollieFlowKind = "checkout" | "banktransfer";

export function getMollieFlowKind(method?: string | null): MollieFlowKind {
    return method === "banktransfer" ? "banktransfer" : "checkout";
}

export function isMollieBanktransfer(method?: string | null): boolean {
    return getMollieFlowKind(method) === "banktransfer";
}

function isFutureDate(value?: string | Date | null, now: Date = new Date()): boolean {
    if (!value) return false;
    const parsed = value instanceof Date ? value : new Date(value);
    if (isNaN(parsed.getTime())) return false;
    return parsed.getTime() > now.getTime();
}

/**
 * Banktransfer hat eine eigene Mollie-Frist. Mollie meldet bei der SEPA-
 * Überweisung früh "expired", obwohl der Zahlungslink laut `linkExpiresAt` noch
 * gültig ist. Wir drehen den Status dann auf "open", damit der Admin einen noch
 * gueltigen Mollie-Versuch nicht fälschlich als "Abgelaufen" sieht.
 *
 * ── Aufrufer / Datenquelle (WS-5-Abgleich, Untersuchung 2026-06-28) ──────────
 * Alle drei Aufrufer (`MolliePaymentSection`, `AttemptTimelineCard`, `ActionBar`)
 * erhalten `status` aus `GetBookingByIdWithCourseLessonBookings` →
 * Booking-FieldResolver `molliePaymentAttempts` → der ROHE persistierte
 * `MolliePaymentAttempt.status` aus der DB. Dieser Pfad durchläuft KEIN
 * `buildStatusResponse` / `normalizeBanktransferExpiryStatus` (das passiert nur
 * in der separaten `MolliePaymentStatus`-Status-Query). Der persistierte Status
 * wird zwar ausschließlich von Mollie gesetzt (`applyMolliePaymentUpdate`,
 * forward-only), ist aber NICHT live-refreshed — der Booking-Overview-Pfad ist
 * also ein Nicht-Live-/Cache-DB-äquivalenter Pfad.
 *
 * ── Spiegelung & Grenze (WS-5) ───────────────────────────────────────────────
 * Diese Funktion spiegelt daher bewusst die SERVER-Cache/DB-Regel
 * `normalizeBanktransferExpiryStatus(isLiveMollieStatus=false)` (expired→open,
 * solange linkExpiresAt in der Zukunft) — NICHT die WS-5-Live-Regel
 * (`isLiveMollieStatus=true`, die Mollies live gemeldetes "expired" mit nur
 * kleinem Clock-Skew-Puffer respektiert). Begründung: Der hier gelesene Status
 * ist nicht live, ein Clock-Skew-Puffer wäre für einen Minuten/Stunden alten
 * DB-Wert semantisch sinnlos.
 *
 * GRENZE: Ein live von Mollie autoritativ gemeldetes "expired" (z.B. manuelle
 * Archivierung im Mollie-Dashboard VOR Ablauf von linkExpiresAt) wird im
 * Booking-Overview erst sichtbar, sobald die Live-`MolliePaymentStatus`-Query
 * (Detail-/EUA-Polling) den autoritativen Status nachzieht oder linkExpiresAt
 * verstreicht. Das ist akzeptiert und konsistent mit dem Server-Cache/DB-Pfad,
 * der denselben Posten ebenfalls bis dahin als "open" führt. Den Override hier
 * generell zu entfernen würde das bewusste banktransfer-UX (gültiger Link =
 * zahlbar) regressieren; eine echte Auflösung bräuchte den Live-Status, den
 * dieser Pfad nicht hat.
 */
export function getDisplayMollieStatus(
    status: string,
    method?: string | null,
    linkExpiresAt?: string | Date | null,
    now: Date = new Date(),
): string {
    if (isMollieBanktransfer(method) && status === "expired" && isFutureDate(linkExpiresAt, now)) {
        return "open";
    }

    return status;
}
